import Service, { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';

import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import { DataCenter } from 'cio/models/account';
import { unixNowSeconds } from 'cio/utils/time';

export interface AccountSimpleInfoInterface {
  id: number;
  name: string;
  environmentIds: number[];
  dataCenter: string;
}

const LIST_EXPIRES_AFTER_SECONDS = 30 * 60;

export default class AccountsListService extends Service {
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;

  @tracked accounts: AccountSimpleInfoInterface[] = [];
  @tracked lastLoaded = 0;

  getDataCenterByEnvironmentId(envId: number | null): DataCenter | null {
    if (envId == null) {
      return null;
    }
    let account = this.accounts.find((account) => account.environmentIds.includes(envId));
    return account ? (account.dataCenter as DataCenter) : null;
  }

  getDataCenterByAccountId(accountId: number | null): DataCenter | null {
    let account = this.accounts.find((account) => account.id === accountId);
    return account ? (account.dataCenter as DataCenter) : null;
  }

  getAccountNameByEnvironmentId(envId: number | null): string | null {
    if (envId == null) {
      return null;
    }
    let account = this.accounts.find((account) => account.environmentIds.includes(envId));
    return account ? account.name : null;
  }

  getAccountNameByAccountId(accountId: number | null): string | null {
    let account = this.accounts.find((account) => account.id === accountId);
    return account ? account.name : null;
  }

  get isExpired() {
    let secondsAfterLastLoad = unixNowSeconds() - this.lastLoaded;
    return secondsAfterLastLoad > LIST_EXPIRES_AFTER_SECONDS;
  }

  fetchAccountsList = task({ drop: true }, async () => {
    this.accounts = [];
    try {
      let response = await this.api.request('admin/accounts', { pathScope: 'global' });
      this.lastLoaded = unixNowSeconds();
      this.accounts = response.map((account: Record<string, any>) => {
        let accountInfo = {
          id: Number(account.id),
          name: account.name,
          environmentIds: account.environment_ids,
          dataCenter: account.data_center,
        };
        accountInfo.environmentIds = account.environment_ids.map((id: string) => Number(id));
        return accountInfo;
      });
    } catch (e) {
      this.apiErrors.report(e);
    }
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    'accounts-list': AccountsListService;
  }
}
